import type { FormEvent } from 'react'
import type { FormField, FormStyle } from '@/api/forms'

/** What FormView needs to draw a form — the public form, or the editor's unsaved draft. */
export interface FormLook {
  name: string
  description?: string | null
  hideTitle?: boolean
  fields: FormField[]
  style?: FormStyle
  submitLabel?: string | null
}

export type FormValues = Record<string, string | boolean | string[]>

const fieldCls =
  'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40'

function inputType(type: string): string {
  switch (type) {
    case 'email':
    case 'tel':
    case 'date':
    case 'number':
      return type
    default:
      return 'text'
  }
}

/** The browser's autofill hint for the contact slots, so a visitor's phone fills them in. */
const AUTOCOMPLETE: Record<string, string> = {
  first_name: 'given-name',
  last_name: 'family-name',
  email: 'email',
  phone: 'tel',
  mobile: 'tel',
}

/** The id tying a field's label to its input (for screen readers and tapping the label). */
const fieldId = (key: string) => `ff-${key}`

function FieldInput({
  field,
  value,
  onChange,
}: {
  field: FormField
  value: string | boolean | string[] | undefined
  onChange: (v: string | boolean | string[]) => void
}) {
  if (field.type === 'radio') {
    return (
      <div className="space-y-1">
        {(field.options ?? []).map((o) => (
          <label key={o} className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="radio"
              name={field.key}
              required={field.required}
              checked={value === o}
              onChange={() => onChange(o)}
              className="h-4 w-4"
            />
            {o}
          </label>
        ))}
      </div>
    )
  }
  if (field.type === 'multiselect') {
    const selected = Array.isArray(value) ? value : []
    return (
      <div className="space-y-1">
        {(field.options ?? []).map((o) => (
          <label key={o} className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={selected.includes(o)}
              onChange={(e) => onChange(e.target.checked ? [...selected, o] : selected.filter((x) => x !== o))}
              className="h-4 w-4"
            />
            {o}
          </label>
        ))}
      </div>
    )
  }
  if (field.type === 'textarea') {
    return (
      <textarea
        id={fieldId(field.key)}
        required={field.required}
        value={(value as string) ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className={fieldCls}
      />
    )
  }
  if (field.type === 'select') {
    return (
      <select
        id={fieldId(field.key)}
        required={field.required}
        value={(value as string) ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className={`${fieldCls} bg-white`}
      >
        <option value="">— select —</option>
        {(field.options ?? []).map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    )
  }
  if (field.type === 'checkbox') {
    return (
      <input
        type="checkbox"
        required={field.required}
        checked={value === true}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4"
      />
    )
  }
  return (
    <input
      id={fieldId(field.key)}
      autoComplete={AUTOCOMPLETE[field.key]}
      type={inputType(field.type)}
      required={field.required}
      value={(value as string) ?? ''}
      onChange={(e) => onChange(e.target.value)}
      className={fieldCls}
    />
  )
}


/**
 * The form a visitor fills in — title, description, fields, button — in the form's own colours. The public
 * page and the builder's live preview both render this, so the preview is exactly what visitors see.
 */
export default function FormView({
  form,
  values,
  onChange,
  onSubmit,
  sending = false,
  error,
}: {
  form: FormLook
  values: FormValues
  onChange: (key: string, value: string | boolean | string[]) => void
  onSubmit: (e: FormEvent) => void
  sending?: boolean
  error?: string | null
}) {
  const style = form.style ?? {}
  return (
    <div style={{ color: style.text }}>
      {!form.hideTitle && (
        <h1 className={`text-xl font-semibold ${style.text ? '' : 'text-brand-darkest'} ${form.description ? 'mb-1' : 'mb-5'}`}>
          {form.name}
        </h1>
      )}
      {form.description && (
        <p className={`text-sm mb-5 ${style.text ? 'opacity-80' : 'text-gray-500'}`}>{form.description}</p>
      )}
      <form onSubmit={onSubmit} className="space-y-3">
        {form.fields.filter((f) => !f.hidden).map((f) =>
          f.type === 'checkbox' ? (
            <label key={f.key} className={`flex items-center gap-2 text-sm ${style.text ? '' : 'text-gray-600'}`}>
              <FieldInput field={f} value={values[f.key]} onChange={(v) => onChange(f.key, v)} />
              {f.label}
              {f.required ? ' *' : ''}
            </label>
          ) : (
            <div key={f.key}>
              <label
                htmlFor={['radio', 'multiselect'].includes(f.type) ? undefined : fieldId(f.key)}
                className={`block text-xs font-medium mb-1 ${style.text ? 'opacity-80' : 'text-gray-500'}`}
              >
                {f.label}
                {f.required ? ' *' : ''}
              </label>
              <FieldInput field={f} value={values[f.key]} onChange={(v) => onChange(f.key, v)} />
            </div>
          ),
        )}
        <button
          type="submit"
          disabled={sending}
          style={{ backgroundColor: style.accent, color: style.buttonText }}
          className={`w-full py-2.5 rounded-lg text-sm font-medium disabled:opacity-60 ${
            style.accent ? 'hover:opacity-90' : 'bg-brand-primary hover:bg-brand-dark'
          } ${style.buttonText ? '' : 'text-white'}`}
        >
          {sending ? 'Sending…' : form.submitLabel || 'Submit'}
        </button>
        {error && <p className="text-sm text-red-600 text-center">{error}</p>}
      </form>
    </div>
  )
}
